import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  FulfillmentOrderApiService,
  FulfillmentOrderStatus,
  SearchFulfillmentOrderRequest,
  SearchFulfillmentOrderResponse,
  SortDirection,
} from '@vendasta/order-fulfillment';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { SimpleUrlHighlight } from './highlights.interface';

@Injectable()
export class HighlightsService {
  constructor(
    private workOrderService: FulfillmentOrderApiService,
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    private translateService: TranslateService,
  ) {}

  getWorkOrderAccountCount(): Observable<SimpleUrlHighlight[]> {
    return this.partnerId$.pipe(
      switchMap((partnerId) =>
        this.workOrderService
          .searchFulfillmentOrder(
            new SearchFulfillmentOrderRequest({
              pageSize: 100,
              resellerPartnerIds: [partnerId],
              statuses: [FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_DETAILS_NEEDED],
              timeOptions: { timeField: 0 },
              sortField: 'created',
              sortDirection: SortDirection.SORT_DIRECTION_DESCENDING,
              projectionFilter: { includeBusinessInfo: true },
            }),
          )
          .pipe(catchError(() => of({ fulfillmentOrders: [] } as SearchFulfillmentOrderResponse))),
      ),
      map((workOrderSearchResponse: SearchFulfillmentOrderResponse) => {
        const queryParams = {
          statuses: ['DISPLAYED_STATUS_VALUES.DETAILS_NEEDED'],
        };
        const queryParamString = JSON.stringify(queryParams);

        if (!workOrderSearchResponse?.totalResults) {
          return [];
        }

        const ordersCount: string =
          workOrderSearchResponse.totalResults > 50 ? '50+' : workOrderSearchResponse.totalResults.toString();

        const highlightDescription =
          workOrderSearchResponse.totalResults === 1
            ? 'WORK_ORDERS.HIGHLIGHTS.ORDER_DESCRIPTION_ONE'
            : 'WORK_ORDERS.HIGHLIGHTS.ORDER_DESCRIPTION_MULTIPLE';
        return [
          {
            statusIcon: 'description',
            description: this.translateService.instant(highlightDescription, { ordersCount }),
            routerLinkUrl: `/order-management`,
            routerLinkQueryParams: { 'order-fufillment-table': queryParamString, tab: 'fulfillment' },
            url: '',
            statusIconClass: '',
            value: undefined,
          } as SimpleUrlHighlight,
        ];
      }),
    );
  }

  getHighlights(): Observable<SimpleUrlHighlight[]> {
    const woHighlights$: Observable<SimpleUrlHighlight[]> = this.getWorkOrderAccountCount();
    return woHighlights$.pipe(map((woHighlights) => woHighlights || []));
  }

  isGreaterThanZero(value: number): boolean {
    return value > 0;
  }
}
